import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import ListAltIcon from '@mui/icons-material/ListAlt'
import { useParams } from 'react-router-dom'
import CircularProgress from '@mui/material/CircularProgress'

import { AdminUITheme } from 'types/AdminUiThemeMui'
import PRMActivityNoteCard from 'components/ParticipantProfile/PRMActivityNoteCard'
import { ParticipantProfileContext } from 'containers/ParticipantProfile'
import { NoteType } from 'types/ViewNotes'
import PRMActivityNoteFilter from 'components/ParticipantProfile/PRMActivityNoteFilter'
import { DEFAULT_PRM_ACTIVITIES_NOTES_COUNT } from 'lib/GlobalConstants'
import useInfiniteScroll from 'lib/hooks/UseInfiniteScroll'
import viewNoteAPI from 'api/Participant/ViewNotes/Action'

const ViewNotes = () => {
  const { id } = useParams() as { id: string }
  const [pageNumber, setPageNumber] = useState(1)
  const { projects, participantNotes } = useContext(
    ParticipantProfileContext,
  ).state
  const { notesProjectId, newNotesCount } = participantNotes
  const { setProjectIdForNotesFilter } = useContext(
    ParticipantProfileContext,
  ).functions
  useEffect(() => {
    setProjectIdForNotesFilter('')
  }, [])

  useEffect(() => {
    setPageNumber(1)
  }, [notesProjectId, newNotesCount])

  const {
    loading,
    data: notes,
    hasMore,
  } = useInfiniteScroll(viewNoteAPI.viewNotes, {
    participantId: id,
    projectId: notesProjectId,
    page: pageNumber,
    perPage: DEFAULT_PRM_ACTIVITIES_NOTES_COUNT,
    newNotesCount: newNotesCount,
  })

  const observerRef = useRef<any>(null)

  const lastItemRef = useCallback(
    (node: HTMLElement | null) => {
      if (loading) return
      if (observerRef.current) observerRef.current.disconnect()
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPageNumber((prevPageNumber) => prevPageNumber + 1)
        }
      })
      if (node) observerRef.current.observe(node)
    },
    [loading, hasMore],
  )
  const { t } = useTranslation()
  const theme: AdminUITheme = useTheme()
  const isActivitiesInMultipleOfDefaultCount =
    notes.length === 0 ||
    notes.length % DEFAULT_PRM_ACTIVITIES_NOTES_COUNT === 0

  return (
    <Box
      data-testid="view-notes"
      style={theme.ParticipantGrid.activityNotesBG}
      key={`PRMActivityNoteFilter${newNotesCount}`}
    >
      <PRMActivityNoteFilter
        projects={projects}
        setProjectIdForFilter={setProjectIdForNotesFilter}
      />

      {!hasMore && notes.length === 0 && (
        <Typography textAlign="center">
          {t('participant.viewNotes.noNotesFound')}
        </Typography>
      )}

      {
        <>
          {notes.map((note: NoteType, index: number) => {
            const { id, content, adminName, projectName, createdAt } = note
            if (notes.length === index + 1) {
              return (
                <div ref={lastItemRef} key={id}>
                  <PRMActivityNoteCard
                    Icon={ListAltIcon}
                    key={id}
                    content={content}
                    trackableName={adminName}
                    projectName={projectName}
                    createdAt={createdAt}
                    isNote={true}
                  />
                </div>
              )
            } else {
              return (
                <div key={id}>
                  <PRMActivityNoteCard
                    Icon={ListAltIcon}
                    key={id}
                    content={content}
                    trackableName={adminName}
                    projectName={projectName}
                    createdAt={createdAt}
                    isNote={true}
                  />
                </div>
              )
            }
          })}
          {hasMore && loading && isActivitiesInMultipleOfDefaultCount && (
            <Box style={theme.ParticipantGrid.displayFlexWithCenter}>
              <CircularProgress size={'2.125rem'} />
            </Box>
          )}
        </>
      }
    </Box>
  )
}

export default ViewNotes
